
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
// Add the ProfileBadge type import
import { ProfileBadge } from "./badges/BadgeDisplay";

export interface ProfileActivity {
  id?: string;
  type: "profile" | "skill" | "project" | "achievement" | "badge" | "certification" | "client-thanks";
  content: string;
  timestamp?: string;
  date?: string; // Added date field for display purposes
  relatedBadgeId?: string; // New: For activities related to earning badges
}

export interface ProfileState {
  profileData: {
    name: string;
    title: string;
    bio: string;
    avatarUrl?: string;
    location: string;
    rating: number;
    earnings: string;
    completedProjects: number;
    availability: "available" | "busy" | "offline";
    skills: string[];
    achievements?: ProfileBadge[]; // Add optional achievements array
    certifications?: ProfileBadge[]; // Add optional certifications array
    clientBadges?: ProfileBadge[]; // Add optional client badges array
    recentActivity: ProfileActivity[]; // Add recentActivity array to profileData
    socialLinks: {
      github: string;
      website: string;
    };
  };
  activities: ProfileActivity[];
  updateProfileData: (data: Partial<ProfileState["profileData"]>) => void;
  updateSkills: (skills: string[]) => void;
  updateAchievements?: (achievements: ProfileBadge[]) => void; // New method for updating achievements
  updateClientBadges?: (badges: ProfileBadge[]) => void; // New method for updating client badges
  updateCertifications?: (certifications: ProfileBadge[]) => void; // New method for updating certifications
  updateAvailability: (status: "available" | "busy" | "offline") => void;
  updateAvatarUrl: (url: string) => void;
  addActivity: (activity: Omit<ProfileActivity, "id" | "timestamp">) => void;
  addBadge: (badge: Omit<ProfileBadge, "id" | "earnedDate">, type: "achievement" | "certification" | "client") => void; // New method to add badges
}

// Sample recent activities
const sampleActivities: ProfileActivity[] = [
  {
    id: "ra1",
    type: "project",
    content: "Completed a React dashboard project",
    date: "2 days ago"
  },
  {
    id: "ra2",
    type: "skill",
    content: "Added TypeScript to skills",
    date: "1 week ago"
  },
  {
    id: "ra3",
    type: "certification",
    content: "Earned AWS Solutions Architect certification",
    date: "2 weeks ago"
  },
  {
    id: "ra4",
    type: "profile",
    content: "Updated profile information",
    date: "1 month ago"
  },
  {
    id: "ra5",
    type: "badge",
    content: "Earned Top Rated badge",
    date: "1 month ago"
  },
  {
    id: "ra6",
    type: "client-thanks",
    content: "Received client appreciation from Sarah Johnson",
    date: "2 months ago"
  }
];

const initialProfileData = {
  name: "Alex Johnson",
  title: "Senior Software Engineer",
  bio: "Passionate about building scalable and maintainable web applications. Specializing in React, Node.js, and cloud technologies.",
  avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=Alex",
  location: "San Francisco, CA",
  rating: 4.8,
  earnings: "$120,000+",
  completedProjects: 35,
  availability: "available" as const,
  skills: ["React", "Node.js", "JavaScript", "TypeScript", "AWS", "GraphQL"],
  recentActivity: sampleActivities, // Add sample activity data
  socialLinks: {
    github: "https://github.com/alexjohnson",
    website: "https://alexjohnson.dev",
  },
};

export const useProfileStore = create<ProfileState>()(
  persist(
    (set) => ({
      profileData: initialProfileData,
      activities: [],
      updateProfileData: (data) =>
        set((state) => ({
          profileData: { ...state.profileData, ...data },
        })),
      updateSkills: (skills: string[]) =>
        set((state) => ({
          profileData: { ...state.profileData, skills: skills },
        })),
      updateAchievements: (achievements: ProfileBadge[]) =>
        set((state) => ({
          profileData: { ...state.profileData, achievements: achievements },
        })),
      updateClientBadges: (badges: ProfileBadge[]) =>
        set((state) => ({
          profileData: { ...state.profileData, clientBadges: badges },
        })),
      updateCertifications: (certifications: ProfileBadge[]) =>
        set((state) => ({
          profileData: { ...state.profileData, certifications: certifications },
        })),
      updateAvailability: (status: "available" | "busy" | "offline") =>
        set((state) => ({
          profileData: { ...state.profileData, availability: status },
        })),
      updateAvatarUrl: (url: string) =>
        set((state) => ({
          profileData: { ...state.profileData, avatarUrl: url },
        })),
      addActivity: (activity) =>
        set((state) => {
          const newActivity = { 
            ...activity, 
            id: uuidv4(), 
            timestamp: new Date().toISOString(),
            date: "Just now"
          };
          return {
            activities: [newActivity, ...state.activities],
            profileData: {
              ...state.profileData,
              recentActivity: [newActivity, ...state.profileData.recentActivity.slice(0, 4)]
            }
          };
        }),
      addBadge: (badge, type) => 
        set((state) => {
          const newBadge = {
            ...badge,
            id: uuidv4(),
            earnedDate: new Date().toISOString()
          };
          
          let activityContent = "";
          let activityType: ProfileActivity["type"] = "badge";
          
          switch (type) {
            case "achievement":
              activityContent = `Earned ${badge.name} achievement`;
              activityType = "achievement";
              return {
                profileData: {
                  ...state.profileData,
                  achievements: [
                    ...(state.profileData.achievements || []),
                    newBadge
                  ]
                },
                activities: [
                  {
                    id: uuidv4(),
                    type: activityType,
                    content: activityContent,
                    timestamp: new Date().toISOString(),
                    date: "Just now",
                    relatedBadgeId: newBadge.id
                  },
                  ...state.activities
                ]
              };
            case "certification":
              activityContent = `Earned ${badge.name} certification`;
              activityType = "certification";
              return {
                profileData: {
                  ...state.profileData,
                  certifications: [
                    ...(state.profileData.certifications || []),
                    newBadge
                  ]
                },
                activities: [
                  {
                    id: uuidv4(),
                    type: activityType,
                    content: activityContent,
                    timestamp: new Date().toISOString(),
                    date: "Just now",
                    relatedBadgeId: newBadge.id
                  },
                  ...state.activities
                ]
              };
            case "client":
              activityContent = badge.clientName 
                ? `Received ${badge.name} badge from ${badge.clientName}` 
                : `Received ${badge.name} client badge`;
              activityType = "client-thanks";
              return {
                profileData: {
                  ...state.profileData,
                  clientBadges: [
                    ...(state.profileData.clientBadges || []),
                    newBadge
                  ]
                },
                activities: [
                  {
                    id: uuidv4(),
                    type: activityType,
                    content: activityContent,
                    timestamp: new Date().toISOString(),
                    date: "Just now",
                    relatedBadgeId: newBadge.id
                  },
                  ...state.activities
                ]
              };
            default:
              return state;
          }
        })
    }),
    {
      name: 'profile-storage',
    }
  )
);
